export default function Footer() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
      {/* CTA Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-20 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
            Ready to Transform Your Home?
          </h2>
          <p className="text-base md:text-xl text-slate-300 mb-8 md:mb-10 max-w-2xl mx-auto">
            Contact us today for a free consultation and estimate. Let's bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 md:gap-4">
            <a 
              href="tel:586-443-0294"
              className="inline-flex items-center justify-center bg-teal-600 text-white px-8 md:px-10 py-4 md:py-5 rounded-full text-base md:text-lg font-semibold hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto"
            >
              <i className="ri-phone-line mr-3 text-lg md:text-xl"></i>
              Call 586-443-0294
            </a>
            <button 
              onClick={() => scrollToSection('estimate')}
              className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm text-white px-8 md:px-10 py-4 md:py-5 rounded-full text-base md:text-lg font-semibold hover:bg-white/20 transition-all border border-white/30 cursor-pointer whitespace-nowrap w-full sm:w-auto"
            >
              <i className="ri-file-list-3-line mr-3 text-lg md:text-xl"></i>
              Get Free Estimate
            </button>
          </div>
        </div>
      </div>

      {/* Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-8 md:mb-12">
          {/* Company Info */}
          <div>
            <img 
              src="https://static.readdy.ai/image/f3dd92fd3ac15212060575a62ab1f264/94a118495bd5e749025b855e49531798.webp"
              alt="American Dream Remodeling INC" 
              className="h-20 md:h-24 w-auto mb-4 md:mb-6"
            />
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Expert bathroom remodeling, basement finishing, and tile installation across Southeast Michigan.
            </p>
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-5">
              <i className="ri-shield-check-line text-teal-400"></i>
              <span>Insured</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-400 text-sm">Find us on:</span>
              <a
                href="https://www.facebook.com/AmericanDreamRemodelingINC"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-teal-600 transition-colors cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-white text-base"></i>
              </a>
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-teal-600 transition-colors cursor-pointer"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-white text-base"></i>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Services</h3>
            <ul className="space-y-3">
              <li>
                <a href="/services/bathroom-renovation" className="text-slate-400 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  Bathroom Remodeling
                </a>
              </li>
              <li>
                <a href="/services/basement-finishing" className="text-slate-400 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  Basement Finishing
                </a>
              </li>
              <li>
                <a href="/services/custom-carpentry" className="text-slate-400 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  Tile Installation
                </a>
              </li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Service Areas</h3>
            <ul className="space-y-3">
              <li className="text-slate-400 text-sm">Macomb County</li>
              <li className="text-slate-400 text-sm">Oakland County</li>
              <li className="text-slate-400 text-sm">Wayne County</li>
              <li className="text-slate-400 text-sm">Washtenaw County</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Contact</h3>
            <ul className="space-y-4">
              <li>
                <a href="tel:586-443-0294" className="flex items-center text-slate-400 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  <i className="ri-phone-line mr-2"></i>
                  586-443-0294
                </a>
              </li>
              <li>
                <button onClick={() => scrollToSection('estimate')} className="flex items-center text-slate-400 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  <i className="ri-mail-line mr-2"></i>
                  Request Estimate
                </button>
              </li>
              <li className="flex items-center text-slate-400 text-sm">
                <i className="ri-map-pin-line mr-2"></i>
                Southeast Michigan
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-6 md:pt-8 flex flex-col md:flex-row justify-between items-center gap-4 md:gap-4">
          <p className="text-slate-400 text-xs md:text-sm text-center md:text-left">
            © 2024 American Dream Remodeling INC. All rights reserved.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6">
            <div className="flex items-center flex-wrap justify-center gap-4 md:gap-6">
              <button onClick={() => scrollToSection('home')} className="text-slate-400 hover:text-teal-400 transition-colors text-xs md:text-sm cursor-pointer">
                Home
              </button>
              <button onClick={() => scrollToSection('services')} className="text-slate-400 hover:text-teal-400 transition-colors text-xs md:text-sm cursor-pointer">
                Services
              </button>
              <button onClick={() => scrollToSection('gallery')} className="text-slate-400 hover:text-teal-400 transition-colors text-xs md:text-sm cursor-pointer">
                Gallery
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-400 hover:text-teal-400 transition-colors text-xs md:text-sm cursor-pointer">
                Contact
              </button>
            </div>
            <div className="flex items-center gap-3 sm:border-l sm:border-white/10 sm:pl-6">
              <a
                href="https://www.facebook.com/AmericanDreamRemodelingINC"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-teal-600 transition-colors cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-white text-sm"></i>
              </a>
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-teal-600 transition-colors cursor-pointer"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-white text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}